import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../css/others/Card.css";
import "../css/others/Table.css";
import BackButton from "../Reusablecomponents/BackButton";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import { CampaignDetailsInfo, CampaignType, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import AuditScopePopup from "../popups/AuditScopePopup";
const CreateCampaign: React.FC = () => {
  const [campaign, setCampaign] = useState<CampaignDetailsInfo>({ campaignName: "", accountName: [], campaignType: "", auditType: "", auditObjective: "", auditScope: "", auditTeam: "", campaignStartDate: "", campaignEndDate: "" });
  const [campaignTypes, setCampaignTypes] = useState<CampaignType[]>([]);
  const [auditTypes, setAuditTypes] = useState<CampaignType[]>([]);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [reviewDuration, setReviewDuration] = useState<number>(0);
  const [, setExtensionDays] = useState<number>(0);
  const [minStartDate, setMinStartDate] = useState<string>("");
  const navigate = useNavigate();
  const clientId = localStorage.getItem("clientId");
  const createdBy = localStorage.getItem("email");
  const providerId = localStorage.getItem("providerId");
  const [frequency, setFrequency] = useState<number | null>(null);
  const [accountOptions, setAccountOptions] = useState<string[]>([]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [usedAccounts, setUsedAccounts] = useState<string[]>([]);
  const [isAuditScopePopupOpen, setIsAuditScopePopupOpen] = useState(false);
  const [selectedAuditControls, setSelectedAuditControls] = useState<string[]>([]);
  const handleAccountChange = (selectedAccounts: string[]) => {
    setCampaign((prev) => ({ ...prev, accountName: selectedAccounts }));
  };
  const toLocalYMD = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".account-dropdown")) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const fetchCampaignTypes = useCallback(async () => {
    try {
      const response = await apiCall("get", "/api/enums/CampaignTypeEnum");
      const filtered = response.filter((item: any) => ["NORMAL", "ADHOC"].includes(item.value));
      setCampaignTypes(filtered);
    } catch (error) {
      console.error("Failed to fetch campaign types", error);
    }
  }, []);
  const fetchAuditTypes = useCallback(async () => {
    try {
      const response = await apiCall("get", "/api/enums/AuditTypeEnum");
      setAuditTypes(response);
    } catch (error) {
      console.error("Failed to fetch audit types", error);
    }
  }, []);
  const fetchClientConfig = useCallback(async () => {
    try {
      const response = await apiCall("get", `/api/client-config/${clientId}`);
      setReviewDuration(response.campaignQcReviewDuration || 0);
      setExtensionDays(response.extensionDays || 0);
      setFrequency(response.projectPickUpIntervel || 0);
    } catch (error) {
      console.error("Failed to fetch client config", error);
      setReviewDuration(0);
      setExtensionDays(0);
      setFrequency(0);
    }
  }, [clientId]);
  const fetchAccountDropdown = useCallback(async () => {
    try {
      const response = await apiCall("get", `api/projects/dropdown-filter?clientId=${clientId}`);
      setAccountOptions(response?.filters?.accountName || []);
    } catch (error) {
      console.error("Failed to fetch account dropdown", error);
    }
  }, [clientId]);
  const fetchExistingCampaigns = useCallback(async () => {
    try {
      const response = await apiCall("get", `/api/campaigns/all?clientId=${clientId}`);
      const accountsInUse: string[] = [];
      response.content.forEach((campaign: any) => {
        if (campaign.campaignStatus !== "CLOSED" &&
          campaign.accountName &&
          Array.isArray(campaign.accountName)) {
          accountsInUse.push(...campaign.accountName);
        }
      });
      const uniqueAccounts = [...new Set(accountsInUse)];
      setUsedAccounts(uniqueAccounts);
    } catch (error) {
      console.error("Failed to fetch existing campaigns", error);
      setUsedAccounts([]);
    }
  }, [clientId]);
  useEffect(() => { fetchCampaignTypes() }, [fetchCampaignTypes]);
  useEffect(() => { fetchAuditTypes() }, [fetchAuditTypes]);
  useEffect(() => { fetchClientConfig() }, [fetchClientConfig]);
  useEffect(() => { fetchExistingCampaigns() }, [fetchExistingCampaigns]);
  useEffect(() => { fetchAccountDropdown() }, [fetchAccountDropdown]);
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setMinStartDate(toLocalYMD(today));
  }, []);
  const isWeekend = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6;
  };
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (campaign.campaignType === "NORMAL" && reviewDuration > 0) {
      const min = new Date(today);
      min.setDate(today.getDate() + reviewDuration + 1);
      setMinStartDate(toLocalYMD(min));
      return;
    }
    setMinStartDate(toLocalYMD(today));
  }, [reviewDuration, campaign.campaignType]);
  const handleEndDateClick = () => {
    if (!campaign.campaignStartDate) {
      setMessage(createToastMessage("Please select a start date first", "warning", 2000));
      return false;
    }
    return true;
  };
  const restrictedChars = /[\\:*?"<>|/]/;
  const containsSpecialChars = (str: string) => {
    return restrictedChars.test(str);
  };
  const handleInputChange = (key: keyof CampaignDetailsInfo, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = event.target;
    if (["campaignName", "auditObjective", "auditScope", "auditTeam"].includes(String(key))) {
      if (value.length > 60) {
        setMessage(createToastMessage("Maximum 60 characters allowed", "warning", 3000));
        return;
      }
      if (containsSpecialChars(value)) {
        setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
        return;
      }
    }
    if (key === "campaignType") {
      if (value === "NORMAL" && reviewDuration > 0) {
        setMessage(createToastMessage(`Note: NORMAL campaigns Start Date can be selected only after ${reviewDuration} days (QC review duration).`, "warning", 2500));
      }
      setCampaign({ ...campaign, [key]: value, campaignStartDate: "", campaignEndDate: "" });
      return;
    }
    if (key === "campaignStartDate") {
      if (isWeekend(value)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);
      if (campaign.campaignType === "NORMAL" && reviewDuration > 0) {
        const minDate = new Date(minStartDate);
        minDate.setHours(0, 0, 0, 0);
        if (selectedDate < minDate) {
          setMessage(createToastMessage(`Start date must be at least ${reviewDuration} days from today`, "warning", 3000));
          return;
        }
      }
      if (campaign.campaignEndDate && new Date(campaign.campaignEndDate) < selectedDate) {
        setCampaign({ ...campaign, [key]: value, campaignEndDate: "" });
      } else {
        setCampaign({ ...campaign, [key]: value });
      }
      return;
    }
    if (key === "campaignEndDate") {
      if (!campaign.campaignStartDate) {
        setMessage(createToastMessage("Please select a start date first", "warning", 3000));
        return;
      }
      if (isWeekend(value)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      if (new Date(value) < new Date(campaign.campaignStartDate)) {
        setMessage(createToastMessage("End date must be after start date", "warning", 3000));
        return;
      }
      setCampaign({ ...campaign, [key]: value });
      return;
    }
    setCampaign({ ...campaign, [key]: value });
  };
  const formatDateTime = (date: string, isStartDate: boolean = false) => {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      let time = "23:59:59";
      if (isStartDate) {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        time = `${hours}:${minutes}:${seconds}`;
      }
      return `${year}-${month}-${day} ${time}`;
    }
    return "";
  };
  const allAccountsUsed = accountOptions.length > 0 && accountOptions.every(acc => usedAccounts.includes(acc));
  const handleSubmit = async () => {
    if (allAccountsUsed) {
      setMessage(createToastMessage("All accounts are currently in use in active campaigns. No more campaigns can be created.", "warning", 3000));
      return;
    }
    const isEmpty = Object.entries(campaign).some(([key, value]) => {
      if (key === "accountName") {
        return !Array.isArray(value) || value.length === 0;
      }
      return value === "";
    });
    if (isEmpty) {
      setMessage(createToastMessage("Please fill all fields", "warning", 3000));
      return;
    }
    const newlyUsedAccounts = campaign.accountName.filter((acc: string) => usedAccounts.includes(acc));
    if (newlyUsedAccounts.length > 0) {
      setMessage(createToastMessage(`Account(s) ${newlyUsedAccounts.join(", ")} are already used in active campaigns`, "warning", 3000));
      return;
    }
    if (campaign.campaignName.length > 50) {
      setMessage(createToastMessage("Campaign Name cannot exceed 50 characters", "warning", 3000));
      return;
    }
    const textFields = ["campaignName", "auditObjective", "auditScope", "auditTeam"];
    for (const field of textFields) {
      if (containsSpecialChars(campaign[field as keyof CampaignDetailsInfo] as string)) {
        setMessage(createToastMessage(`Special characters are not allowed in ${field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}`, "warning", 3000));
        return;
      }
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(campaign.campaignStartDate);
    if (isWeekend(campaign.campaignStartDate)) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    startDate.setHours(0, 0, 0, 0);
    if (campaign.campaignType === "NORMAL" && reviewDuration > 0) {
      const minStart = new Date(today);
      minStart.setDate(today.getDate() + reviewDuration);
      if (startDate < minStart) {
        setMessage(createToastMessage(`Start date must be at least ${reviewDuration} days from today`, "warning", 3000));
        return;
      }
    }
    const endDate = new Date(campaign.campaignEndDate);
    endDate.setHours(0, 0, 0, 0);
    if (endDate <= startDate) {
      setMessage(createToastMessage("End date must be after start date", "warning", 3000));
      return;
    }
    if (isWeekend(campaign.campaignEndDate)) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    try {
      const createdDate = formatDateTime(new Date().toISOString(), true);
      const formattedCampaign: Record<string, any> = { ...campaign, createdBy, clientId, createdDate, campaignStartDate: formatDateTime(campaign.campaignStartDate, true), campaignEndDate: formatDateTime(campaign.campaignEndDate), frequency: frequency, providerId: providerId };
      if (selectedAuditControls.length > 1) {
        formattedCampaign.auditScopeControls = selectedAuditControls;
      }
      await apiCall("post", "/api/campaigns", formattedCampaign);
      setMessage(createToastMessage("Campaign created successfully!", "success", 2800));
      setTimeout(() => { navigate("/campaigns") }, 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to create campaign";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  useEffect(() => {
    if (allAccountsUsed) {
      setMessage(createToastMessage("All accounts are currently in use in active campaigns. No more campaigns can be created until some campaigns are closed.", "warning", 3000));
    }
  }, [allAccountsUsed]);
  return (
    <div className="container">
      <h1 className="title">Create Campaign</h1>
      <div className="grid">
        {Object.entries(campaign).map(([key, value]) => {
          const label = key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
          if (key === "accountName") {
            const isAllSelected = accountOptions.length > 0 && campaign.accountName.length === accountOptions.length;
            const selectedAccountPreview = campaign.accountName.length === 0 ? "Select Account Name" : campaign.accountName.length <= 2 ? campaign.accountName.join(", ") : `${campaign.accountName.slice(0, 2).join(", ")} ...`;
            const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
              e.stopPropagation();
              if (isAllSelected) {
                handleAccountChange([]);
              } else {
                const availableAccounts = accountOptions.filter(acc => !usedAccounts.includes(acc));
                handleAccountChange(availableAccounts);
              }
            };
            const handleCheckboxChange = (acc: string, e: React.ChangeEvent<HTMLInputElement>) => {
              e.stopPropagation();
              if (usedAccounts.includes(acc)) {
                setMessage(createToastMessage(`Account "${acc}" is already used in an active campaign and cannot be selected`, "warning", 3000));
                return;
              }
              let updated = [...campaign.accountName];
              if (updated.includes(acc)) {
                updated = updated.filter((a) => a !== acc);
              } else {
                updated.push(acc);
              }
              handleAccountChange(updated);
            };
            return (
              <div className={`account-card ${showAccountDropdown ? "dropdown-open-card" : ""}`} key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">Account Name</div>
                    <div className="account-dropdown">
                      <div className="dropdown-header" onClick={() => setShowAccountDropdown((prev) => !prev)} title={campaign.accountName.length > 0 ? campaign.accountName.join(", ") : "Select Account Name"}>
                        <span className="dropdown-text">{selectedAccountPreview}</span>
                        <span className="dropdown-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </div>
                      {showAccountDropdown && (
                        <div className="account-dropdown-body" onClick={(e) => e.stopPropagation()}>
                          <div className="dropdown-item">
                            <label className="dropdown-label">
                              <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />Select All
                            </label>
                          </div>
                          {accountOptions.map((acc) => {
                            const isUsed = usedAccounts.includes(acc);
                            return (
                              <div className="dropdown-item" key={acc}>
                                <label className="dropdown-label" style={{ opacity: isUsed ? 0.5 : 1, cursor: isUsed ? "not-allowed" : "pointer" }}>
                                  <input type="checkbox" checked={campaign.accountName.includes(acc)} onChange={(e) => handleCheckboxChange(acc, e)} disabled={isUsed} />{acc} {isUsed && " (In use)"}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "campaignType") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <select value={value} onChange={(event) => handleInputChange(key as keyof CampaignDetailsInfo, event)}>
                      <option value="">Select {label}</option>
                      {campaignTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.description}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "auditType") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <select value={value} onChange={(event) => handleInputChange(key as keyof CampaignDetailsInfo, event)}>
                      <option value="">Select {label}</option>
                      {auditTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.description}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "auditScope") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <div className="audit-scope-input-wrapper" onClick={() => setIsAuditScopePopupOpen(true)}>
                      <input type="text" value={value} readOnly placeholder="Click to select audit scope" />
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "campaignStartDate") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <input type="date" value={value} min={minStartDate} onChange={(event) => handleInputChange(key as keyof CampaignDetailsInfo, event)} />
                  </div>
                </div>
              </div>
            );
          }
          if (key === "campaignEndDate") {
            return (
              <div className="card" key={key} onClick={() => !campaign.campaignStartDate && handleEndDateClick()}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <input type="date" value={value} min={campaign.campaignStartDate || minStartDate} onChange={(event) => handleInputChange(key as keyof CampaignDetailsInfo, event)} />
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div className="card" key={key}>
              <div className="card-inner">
                <div className="content">
                  <div className="label">{label}</div>
                  <input type="text" value={value} onChange={(event) => handleInputChange(key as keyof CampaignDetailsInfo, event)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="buttons">
        <BackButton className="back-button2" label="Back" />
        <button className="submit-button2" onClick={handleSubmit} disabled={allAccountsUsed}>Submit</button>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      {isAuditScopePopupOpen && (
        <AuditScopePopup isOpen={isAuditScopePopupOpen} onClose={() => setIsAuditScopePopupOpen(false)} onSave={(auditScope, controls) => { setCampaign({ ...campaign, auditScope }); setSelectedAuditControls(controls); }} initialValue={campaign.auditScope} />
      )}
    </div>
  );
};
export default CreateCampaign;